// hooks/playground/useAccountSearch.js
import { useState, useEffect, useCallback, useRef } from 'react';

export const useAccountSearch = (lineItems) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const clearTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const results = lineItems.filter(item => {
        const accountName = (item.account_name || '').toLowerCase();
        const fqn = (item.fully_qualified_name || '').toLowerCase();
        return accountName.includes(term) || fqn.includes(term);
      });
      setSearchResults(results);
      setCurrentSearchIndex(results.length > 0 ? 0 : -1);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setHighlightedRow(null);
    }
  }, [searchTerm, lineItems]);

  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, []);

  const handleNextResult = useCallback(() => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
    }
  }, [searchResults.length]);

  const handlePrevResult = useCallback(() => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
    }
  }, [searchResults.length]);

  const handleSelectResult = useCallback((index) => {
    setCurrentSearchIndex(index);
  }, []);

  const clearSearch = useCallback(() => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
    }
    setSearchTerm('');
    setSearchResults([]);
    setCurrentSearchIndex(-1);
    setHighlightedRow(null);
    setIsSearchOpen(false);
  }, []);

  const clearSearchAfterDelay = useCallback(() => {
    setIsSearchOpen(false);
    
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
    }
    clearTimeoutRef.current = setTimeout(() => {
      setSearchTerm('');
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setHighlightedRow(null);
    }, 3500);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    isSearchOpen,
    setIsSearchOpen,
    searchResults,
    currentSearchIndex,
    highlightedRow,
    setHighlightedRow,
    handleNextResult,
    handlePrevResult,
    handleSelectResult,
    clearSearch,
    clearSearchAfterDelay,
  };
};